import { HTMLAttributes, KeyboardEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import styles from './ProfileMenu.module.scss';
import MenuItem from '../../../common/MenuItem/MenuItem';
import { AccountIcon } from '../../../../lib/icons/AccountIcon';
import { headerTestIds } from '../../../../lib/tests/testIds';
import WithDevice from '../../../common/WithDevice/WithDevice';
import { Role, useSession } from '../../../../lib/context/SessionContext';
import { Label } from '../../../common/Label/Label';
import { HEADER_LABEL_KEYS } from '../../../../lib/constants/labelKeys';
import SubItem from '../../../common/MenuItem/SubItem';
import LogoutItem from '../../../common/MenuItem/LogoutItem';
import { useHeaderContext } from '../../../../lib/context/HeaderContext';
import { classes } from '../../../../lib/utils/cssUtils';
import { useDevice } from '../../../../lib/hooks/device';
import { ATTRIBUTES } from '../../../../lib/constants/attributes';
import { useAttributeLabel } from '../../../../lib/hooks/labels';
import { useId } from '../../../../lib/hooks/useId';
import { isNil } from '../../../../lib/utils/isNil';
import { msalLoginRedirect } from '../../../../lib/utils/auth/msal';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';

export const ProfileWelcomeMessage = ({ className, ...rest }: HTMLAttributes<HTMLSpanElement>) => {
  const { payload } = useSession();

  return (
    <span className={classes(styles.message, className)} {...rest}>
      {payload?.role === Role.GUEST ? (
        <Label name={HEADER_LABEL_KEYS.MENU_SIGNUP_LABEL} />
      ) : (
        <>
          <Label name={HEADER_LABEL_KEYS.MENU_WELCOME_LABEL} /> {payload?.user?.firstName}
        </>
      )}
    </span>
  );
};

const ProfileMenu = () => {
  const { profileItems } = useHeaderContext();
  const { payload } = useSession();
  const [isExpanded, setIsExpanded] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const mobileRoot = profileItems[0];
  const menuRef = useRef<HTMLDivElement>(null);
  const device = useDevice();
  const profileIconLabel = useAttributeLabel(ATTRIBUTES.ARIA_LABEL, HEADER_LABEL_KEYS.PROFILE_MENU_ICON_ARIA_LABEL);
  const itemsListId = useId('items-list-');

  const handleRedirectToLogin = async () => {
    setRedirecting(true);
    await msalLoginRedirect();
    setRedirecting(false);
  };

  const handleEnterKey: KeyboardEventHandler<HTMLButtonElement> = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (payload?.role !== Role.GUEST) {
        setIsExpanded(prev => !prev);
      } else {
        handleRedirectToLogin();
      }
    }
  };

  const focusListener = useCallback((event: FocusEvent) => {
    if (!menuRef.current?.contains(event.relatedTarget as Node)) {
      event.preventDefault();
      setIsExpanded(false);
    }
  }, []);

  useEffect(() => {
    if (device === 'desktop') {
      menuRef.current?.addEventListener('focusout', focusListener);
    }

    return () => menuRef.current?.removeEventListener('focusout', focusListener);
  }, [device]);

  return (
    <>
      <WithDevice desktop>
        <div
          data-testid={headerTestIds.profileMenu}
          ref={menuRef}
          className={classes(styles.profileDesktopContainer, { [styles.profileDesktopContainerActive]: !redirecting })}
        >
          {redirecting && <RefreshLoader />}
          <button
            {...(payload?.role === Role.GUEST
              ? { onClick: handleRedirectToLogin }
              : { 'aria-expanded': isExpanded, 'aria-controls': itemsListId })}
            {...profileIconLabel}
            className={styles.iconButton}
            onKeyDown={handleEnterKey}
            {...(redirecting && { disabled: true })}
          >
            <ProfileWelcomeMessage />
            <AccountIcon data-testid={headerTestIds.profileIcon} />
          </button>
          {payload?.role !== Role.GUEST && (
            <ul id={itemsListId} className={classes(styles.profileItems, { [styles.expanded]: isExpanded })}>
              {profileItems.map(({ id, ...props }) => (
                <SubItem key={id} {...props} />
              ))}
              <LogoutItem />
            </ul>
          )}
        </div>
      </WithDevice>
      <WithDevice mobile tablet>
        {payload?.role === Role.GUEST && (
          <MenuItem
            loading={redirecting}
            label={<ProfileWelcomeMessage />}
            icon={<AccountIcon data-testid={headerTestIds.profileIcon} className={styles.mobileGuestWelcomeIcon} />}
            link="#singup"
            onClick={handleRedirectToLogin}
          />
        )}
        {!isNil(mobileRoot) && payload?.role !== Role.GUEST ? (
          <MenuItem
            key={mobileRoot.id}
            label={mobileRoot.label}
            link={mobileRoot.link}
            variant="profile"
            icon={<AccountIcon className={styles.iconMobile} data-testid={headerTestIds.profileIcon} />}
            subItems={profileItems.slice(1)}
          />
        ) : null}
      </WithDevice>
    </>
  );
};

export default ProfileMenu;
